import { scaleLinear } from 'd3-scale';
import { minBy, maxBy } from 'lodash-es';

import { getYValue } from './get-y-value.js';
import { isThresholdDynamic } from './type-guards.js';
import { type ThresholdIndicatorData } from '../../../core/components/threshold-bar/indicators/ThresholdIndicator.js';
import type { ThresholdMarker } from '../../../core/components/threshold-bar/types.js';
import { getMarkerValue } from '../../../core/components/threshold-bar/utils/get-marker-value.js';
import { getThresholdPixel } from '../../../core/components/threshold-bar/utils/get-threshold-pixel.ts.js';
import { THRESHOLD } from '../../../core/messages.js';
import type { MinMax } from '../../../core/types/min-max.js';
import type { AnyNumberScale } from '../../../core/types/scales.js';
import type { AxisIdToScales } from '../../context/XYChartScales.context.js';
import type {
  AxisIdToFormatter,
  XYChartAxisInternal,
  XYChartThresholdsPropsInternal,
} from '../../types/xy-chart-internals.js';
import type {
  XYChartThresholdDataLineStatic,
  XYChartThresholdDataRangeStatic,
  XYChartThresholdDynamicData,
  XYChartYAxisPosition,
} from '../../types/xy-chart.js';
import { getCurrentScale } from '../../utils/get-scale.js';
import { getAxisFormatterWithTimeFallback } from '../overlay/utils/get-axis-formatter-with-time-fallback.js';

interface ThresholdMarkersReducerConfig {
  axis: 'left' | 'right';
  axisConfig?: XYChartAxisInternal;
  axesIdToScales: AxisIdToScales;
  axesIdToFormatter: AxisIdToFormatter;
  width: number;
  axes: XYChartAxisInternal[];
}

export function getDynamicThresholdDomain(
  data: XYChartThresholdDynamicData[],
  side: 'left' | 'right',
  visibleDomain: MinMax,
) {
  const minXPoint = findPointWithMinX(data);
  const maxXPoint = findPointWithMaxX(data);

  const targetXDomain = side === 'left' ? visibleDomain.min : visibleDomain.max;

  if (targetXDomain <= minXPoint.x.valueOf()) {
    return getYRangeFromPoint(minXPoint);
  }
  if (targetXDomain >= maxXPoint.x.valueOf()) {
    return getYRangeFromPoint(maxXPoint);
  }
  return interpolateYRange(data, targetXDomain);
}

function findPointWithMinX(data: XYChartThresholdDynamicData[]) {
  return minBy(data, (point) => point.x.valueOf()) ?? data[0];
}

function findPointWithMaxX(data: XYChartThresholdDynamicData[]) {
  return maxBy(data, (point) => point.x.valueOf()) ?? data[data.length - 1];
}

function getYRangeFromPoint(point: XYChartThresholdDynamicData) {
  return {
    y0: getYValue(point, 'min'),
    y1: getYValue(point, 'max'),
  };
}
function interpolateYRange(
  data: XYChartThresholdDynamicData[],
  targetX: number,
) {
  const xValues = data.map((point) => Number(point.x));
  const minYValues = data.map((point) => getYValue(point, 'min'));
  const maxYValues = data.map((point) => getYValue(point, 'max'));
  const minYScale = scaleLinear(xValues, minYValues).clamp(true);
  const maxYScale = scaleLinear(xValues, maxYValues).clamp(true);

  return {
    y0: minYScale(targetX),
    y1: maxYScale(targetX),
  };
}

/**
 * Returns a reducer function to accumulate threshold markers with scale and formatted value.
 */
export function markersReducer({
  axis,
  axisConfig,
  axesIdToScales,
  axesIdToFormatter,
  width,
  axes,
}: ThresholdMarkersReducerConfig) {
  return (
    acc: ThresholdMarker[],
    threshold: XYChartThresholdsPropsInternal[number],
  ) => {
    // TODO: workaround to dont let chart break. follow up APPDEV-16553
    // workaround applied after adding the new filtering series | toggling visible axis
    // we should refactor how we manage the threshold instead

    if (!axes.find((axis) => axis.id === threshold.yAxisId)) {
      return acc;
    }

    if (axisPositionIncludes(axis)(threshold.position)) {
      const yScale = getCurrentScale(
        axesIdToScales,
        threshold.yAxisId,
      ) as AnyNumberScale;

      if (isThresholdDynamic(threshold)) {
        const xScale = getCurrentScale(
          axesIdToScales,
          threshold.xAxisId,
        ) as AnyNumberScale;
        const data = threshold.data;
        const minElement = minBy(data, (e) => getYValue(e, 'min'));
        const maxElement = maxBy(data, (e) => getYValue(e, 'max'));

        if (!minElement || !maxElement) {
          return acc;
        }

        acc.push({
          ...threshold,
          y0: getThresholdPixel(getYValue(minElement, 'min'), yScale),
          y1: getThresholdPixel(getYValue(maxElement, 'max'), yScale),
          value: 'dynamic',
          isShadow: true,
        });

        const domain = xScale.domain();
        const xMinMax = {
          min: domain[0].valueOf(),
          max: domain[1].valueOf(),
        };

        const { y0, y1 } = getDynamicThresholdDomain(data, axis, xMinMax);

        acc.push({
          ...threshold,
          y0: getThresholdPixel(y0, yScale),
          y1: getThresholdPixel(y1, yScale),
          value: THRESHOLD.dynamic.defaultMessage,
        });
      } else {
        const markerDomain = {
          y0: getYValue(threshold.data, 'min'),
          y1: getYValue(threshold.data, 'max'),
        };

        acc.push({
          ...threshold,
          y0: getThresholdPixel(markerDomain.y0, yScale),
          y1: getThresholdPixel(markerDomain.y1, yScale),
          value: getMarkerValue(
            markerDomain,
            getAxisFormatterWithTimeFallback({
              axisConfig,
              axisId: threshold.yAxisId,
              scale: yScale,
              axesIdToFormatter,
              width,
            }),
          ),
        });
      }
    }
    return acc;
  };
}

export const axesPositionsIncludes = (
  axisPosition: 'left' | 'right',
  axesPositions: { position: XYChartYAxisPosition }[],
) => {
  const expectedPositionsIncludes = axisPositionIncludes(axisPosition);

  return !!axesPositions.find(({ position }) =>
    expectedPositionsIncludes(position),
  );
};

export const axisPositionIncludes =
  (expectedPosition: 'left' | 'right') =>
  (actualPosition: 'left' | 'right' | 'both') => {
    return [expectedPosition, 'both'].includes(actualPosition);
  };

type IndicatorData = (args: {
  axisIdToScales: AxisIdToScales;
  threshold: XYChartThresholdsPropsInternal[number];
  width: number;
}) => ThresholdIndicatorData[];

export const mapIndicatorDataFrom: IndicatorData = ({
  axisIdToScales,
  threshold,
  width,
}) => {
  const yScale = getCurrentScale(
    axisIdToScales,
    threshold.yAxisId,
  ) as AnyNumberScale;

  if (isThresholdDynamic(threshold)) {
    const data: XYChartThresholdDynamicData[] = threshold.data;

    const xScale = getCurrentScale(
      axisIdToScales,
      threshold.xAxisId,
    ) as AnyNumberScale;

    const initialSegment = {
      x0: 0,
      x1: 0,
      y0: getThresholdPixel(getYValue(data[0], 'min'), yScale),
      y1: getThresholdPixel(getYValue(data[0], 'max'), yScale),
    };

    const finalSegment = {
      x0: width,
      x1: width,
      y0: getThresholdPixel(getYValue(data[data.length - 1], 'min'), yScale),
      y1: getThresholdPixel(getYValue(data[data.length - 1], 'max'), yScale),
    };

    const segments = data.map((coordinate) => ({
      x0: getThresholdPixel(coordinate.x, xScale),
      x1: getThresholdPixel(coordinate.x, xScale),
      y0: getThresholdPixel(getYValue(coordinate, 'min'), yScale),
      y1: getThresholdPixel(getYValue(coordinate, 'max'), yScale),
    }));

    return [initialSegment, ...segments, finalSegment];
  } else {
    const data = threshold.data as
      | XYChartThresholdDataRangeStatic
      | XYChartThresholdDataLineStatic;

    const yMinScaleValue = getThresholdPixel(getYValue(data, 'min'), yScale);
    const yMaxScaleValue = getThresholdPixel(getYValue(data, 'max'), yScale);

    return [
      {
        x0: 0,
        x1: 0,
        y0: yMinScaleValue,
        y1: yMaxScaleValue,
      },
      {
        x0: width,
        x1: width,
        y0: yMinScaleValue,
        y1: yMaxScaleValue,
      },
    ];
  }
};
